import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, History, RotateCcw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang, translateFr as tfr } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { IntelligenceComposer, SCOPES, ACTIONS } from "@/components/intelligence/IntelligenceComposer";
import { StarterPrompts } from "@/components/intelligence/StarterPrompts";
import { RecentAnalysesPanel } from "@/components/intelligence/RecentAnalysesPanel";
import { IntelligenceResponse } from "@/components/intelligence/IntelligenceResponse";

function LoadingBlock({ tf }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-2.5" data-testid="intel-loading" role="status" aria-live="polite">
      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-athlynx animate-pulse" aria-hidden="true" />
        <span className="animate-pulse">{tf("Analyse du portefeuille…")}</span>
      </p>
      <Skeleton className="h-4 w-3/4" /><Skeleton className="h-4 w-1/2" />
    </div>
  );
}

export default function Intelligence() {
  const { user } = useAuth();
  const { lang, tf } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const firstName = user?.name?.split(" ")[0] || "Agent";

  const [thread, setThread] = useState([]); // {role: user|assistant|error, content, structured}
  const [q, setQ] = useState("");
  const [scope, setScope] = useState(SCOPES[0]);
  const [action, setAction] = useState(ACTIONS[0].label);
  const [asking, setAsking] = useState(false);
  const [convId, setConvId] = useState(null);
  const [convs, setConvs] = useState([]);
  const [histOpen, setHistOpen] = useState(false);
  const [contextPlayer, setContextPlayer] = useState(null);
  const [counts, setCounts] = useState(null);
  const endRef = useRef(null);

  const landing = thread.length === 0 && !asking;

  const loadConvs = useCallback(() => {
    api.get("/intelligence/conversations").then((r) => setConvs(r.data.conversations || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/intelligence/overview").then((r) => setCounts(r.data?.counts || null)).catch(() => {});
    loadConvs();
  }, [loadConvs]);

  useEffect(() => {
    const aid = searchParams.get("athlete_id");
    if (!aid) return;
    api.get(`/players/${aid}`).then((r) => {
      const p = r.data.player;
      if (p) setContextPlayer({ id: p.id, name: p.name });
    }).catch(() => toast.error(tfr("Athlète introuvable")));
  }, [searchParams]);

  // Deep link from the Dashboard Intelligence card: restore the exact conversation.
  useEffect(() => {
    const cid = searchParams.get("conversation");
    if (cid) openConv(cid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!landing) endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [thread.length, asking, landing]);

  const clearContext = () => {
    setContextPlayer(null);
    setSearchParams({}, { replace: true });
    toast(tfr("Contexte retiré — retour à la vue portefeuille."));
  };

  const submit = async (text, { raw = false } = {}) => {
    const actionDef = ACTIONS.find((a) => a.label === action) || ACTIONS[0];
    const base = (text ?? q).trim();
    const message = raw ? base : actionDef.build(base).trim();
    if (!message || asking) return;
    setAsking(true);
    setQ("");
    setThread((t) => [...t.filter((m) => m.role !== "error"), { role: "user", content: message }]);
    try {
      const r = await api.post("/intelligence/query", {
        message, scope, conversation_id: convId, player_id: contextPlayer?.id || null, lang,
      });
      setThread((t) => [...t, { role: "assistant", structured: r.data.response }]);
      setConvId(r.data.conversation_id);
      loadConvs();
    } catch (e) {
      setThread((t) => [...t, { role: "error", content: message }]);
      toast.error(tf("Impossible de générer l'analyse pour le moment."));
    } finally { setAsking(false); }
  };

  const pickPrompt = (label) => submit(label, { raw: true });
  const followUp = (question) => submit(question, { raw: true });
  const generatePlan = (name) => submit(`${tfr("Génère un plan d'action opérationnel")}${name ? ` ${tfr("pour")} ${name}` : ""}`, { raw: true });
  const retry = () => {
    const last = [...thread].reverse().find((m) => m.role === "error");
    if (last) submit(last.content, { raw: true });
  };

  const newAnalysis = () => {
    setThread([]);
    setConvId(null);
    setQ("");
    setHistOpen(false);
    setSearchParams({}, { replace: true });
  };

  const openConv = async (cid) => {
    try {
      const r = await api.get(`/intelligence/conversations/${cid}`);
      const msgs = (r.data.messages || []).map((m) => (
        m.role === "assistant"
          ? { role: "assistant", structured: m.structured_response || { summary: m.content, entities: [], deadlines: [], follow_up_questions: [] } }
          : { role: "user", content: m.content }
      ));
      setThread(msgs);
      setConvId(cid);
      if (r.data.player_id && r.data.player_name) setContextPlayer({ id: r.data.player_id, name: r.data.player_name });
      setHistOpen(false);
    } catch { toast.error(tfr("Conversation introuvable")); }
  };

  const renameConv = async (c) => {
    const title = window.prompt(tf("Renommer l'analyse :"), c.title);
    if (!title?.trim()) return;
    await api.patch(`/intelligence/conversations/${c.id}`, { title: title.trim() }).catch(() => toast.error(tfr("Erreur")));
    loadConvs();
  };

  const deleteConv = async (c) => {
    await api.delete(`/intelligence/conversations/${c.id}`).catch(() => toast.error(tfr("Erreur")));
    if (convId === c.id) newAnalysis();
    loadConvs();
  };

  const historyProps = { convs, activeId: convId, onOpen: openConv, onRename: renameConv, onDelete: deleteConv, onNew: newAnalysis };
  const composer = (
    <IntelligenceComposer
      value={q} onChange={setQ} onSubmit={() => submit()} asking={asking}
      scope={scope} onScope={setScope} action={action} onAction={setAction}
      contextPlayer={contextPlayer} onPickContext={setContextPlayer} onClearContext={clearContext}
    />
  );

  return (
    <div className="flex items-stretch" data-testid="intelligence-page">
      {/* Center workspace */}
      <div className="flex-1 min-w-0 flex flex-col min-h-[calc(100vh-64px)]">
        {/* Compact top row (history access below xl) */}
        <div className="flex items-center justify-between gap-2 px-4 md:px-6 pt-4 xl:hidden">
          <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70 font-semibold">Athlynx Intelligence</p>
          <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => setHistOpen(true)} data-testid="intel-history-btn">
            <History className="h-3.5 w-3.5" /> {tf("Historique")}
          </Button>
        </div>

        {landing ? (
          /* ---------- LANDING MODE ---------- */
          <div className="flex-1 flex flex-col justify-center px-4 md:px-8 pb-[6vh] pt-8">
            <div className="w-full max-w-[780px] mx-auto">
              <p className="hidden xl:block text-[10px] uppercase tracking-[0.26em] text-muted-foreground/70 font-semibold text-center" data-testid="intel-eyebrow">
                Athlynx Intelligence
              </p>
              <h1 className="mt-4 text-3xl md:text-[40px] font-bold tracking-tight text-center leading-tight" data-testid="intel-greeting">
                {tf("Bonjour")} {firstName}.
              </h1>
              <h2 className="mt-1 text-lg md:text-2xl font-semibold tracking-tight text-center text-muted-foreground" data-testid="intel-headline">
                {tf("Que souhaitez-vous analyser ?")}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground/90 text-center max-w-md mx-auto">
                {tf("Interrogez votre portefeuille, vos contrats, vos relations et vos opportunités.")}
              </p>

              <div className="mt-8">
                <StarterPrompts onPick={pickPrompt} disabled={asking} />
              </div>

              <div className="mt-5">{composer}</div>

              {counts && counts.players === 0 && (
                <div className="mt-6 text-center" data-testid="intel-empty-state">
                  <p className="text-xs text-muted-foreground">
                    {tf("Athlynx Intelligence s'appuie sur les données de votre espace de travail.")}
                    {tfr("Ajoutez des athlètes, renseignez vos contrats et enregistrez vos échanges pour des analyses plus riches.")}
                  </p>
                  <div className="mt-2 flex items-center justify-center gap-3 text-xs">
                    <a href="/pipeline" className="text-athlynx hover:underline">{tfr("Ajouter des athlètes")}</a>
                    <a href="/import" className="text-athlynx hover:underline">{tfr("Importer des données")}</a>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ---------- CONVERSATION MODE ---------- */
          <div className="flex-1 flex flex-col">
            <div className="flex-1 w-full max-w-[780px] mx-auto px-4 md:px-6 py-5 space-y-4" data-testid="intel-thread">
              {thread.map((m, i) => (
                m.role === "user" ? (
                  <div key={i} className="flex justify-end">
                    <p className="max-w-[85%] rounded-2xl rounded-br-md bg-secondary/70 px-4 py-2.5 text-sm leading-relaxed" data-testid={`intel-msg-user-${i}`}>
                      {m.content}
                    </p>
                  </div>
                ) : m.role === "assistant" ? (
                  <IntelligenceResponse key={i} r={m.structured} onGeneratePlan={generatePlan} onFollowUp={followUp} asking={asking} />
                ) : (
                  <div key={i} className="rounded-2xl border border-danger/30 bg-danger/5 p-4 flex items-center justify-between gap-3" data-testid="intel-error-state">
                    <p className="text-sm text-muted-foreground">{tf("Impossible de générer l'analyse pour le moment.")}</p>
                    <Button variant="outline" size="sm" className="h-8 gap-1.5 shrink-0" onClick={retry} disabled={asking} data-testid="intel-retry-btn">
                      <RotateCcw className="h-3.5 w-3.5" /> {tf("Réessayer")}
                    </Button>
                  </div>
                )
              ))}
              {asking && <LoadingBlock tf={tf} />}
              <div ref={endRef} />
            </div>
            <div className="sticky bottom-0 bg-background/92 backdrop-blur-sm pb-[max(16px,env(safe-area-inset-bottom))] pt-2">
              <div className="w-full max-w-[780px] mx-auto px-4 md:px-6">{composer}</div>
            </div>
          </div>
        )}
      </div>

      {/* Right history rail — desktop */}
      <aside className="hidden xl:block w-[300px] shrink-0 border-l border-border" data-testid="intel-history-rail">
        <div className="sticky top-16 max-h-[calc(100vh-64px)] overflow-y-auto scroll-thin px-4 py-5">
          <RecentAnalysesPanel {...historyProps} />
        </div>
      </aside>

      {/* History drawer — tablet / mobile */}
      <Sheet open={histOpen} onOpenChange={setHistOpen}>
        <SheetContent side="right" className="w-[320px] sm:w-[360px] overflow-y-auto" data-testid="intel-history-sheet">
          <SheetHeader className="sr-only">
            <SheetTitle>{tfr("Analyses récentes")}</SheetTitle>
          </SheetHeader>
          <div className={cn("mt-10 sm:mt-8")}>
            <RecentAnalysesPanel {...historyProps} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
